/**
 * Earnings Dashboard - Shows firm's commission from client subscriptions.
 * Summary cards, client breakdown table, and monthly chart.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  DollarSign, Users, TrendingUp, ArrowUpRight, Loader2,
  Link2, CheckCircle, AlertCircle, ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';

function fmtCurrency(amount: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
}

export default function EarningsPage() {
  const navigate = useNavigate();

  const { data: connectStatus, isLoading: connectLoading } = useQuery({
    queryKey: ['connect-status'],
    queryFn: async () => {
      const r = await apiClient.get('/connect/status');
      return r.data?.data;
    },
  });

  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['earnings-summary'],
    queryFn: async () => {
      const r = await apiClient.get('/earnings/summary');
      return r.data?.data;
    },
    enabled: connectStatus?.connected,
  });

  const { data: clients } = useQuery({
    queryKey: ['earnings-clients'],
    queryFn: async () => {
      const r = await apiClient.get('/earnings/clients');
      return r.data?.data || [];
    },
    enabled: connectStatus?.connected,
  });

  const { data: clientPlans } = useQuery({
    queryKey: ['client-plans'],
    queryFn: async () => {
      const r = await apiClient.get('/client-plans');
      return r.data?.data || [];
    },
  });

  const handleOnboard = async () => {
    try {
      const r = await apiClient.post('/connect/onboard');
      if (r.data?.data?.url) window.location.href = r.data.data.url;
    } catch (err: any) {
      console.error('Connect onboard error:', err);
    }
  };

  const handleSwitchModel = async (model: string) => {
    try {
      await apiClient.post('/connect/switch-model', { billingModel: model });
      window.location.reload();
    } catch (err: any) {
      console.error('Switch model error:', err);
    }
  };

  if (connectLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={DollarSign}
        title="Earnings Dashboard"
        description="Track your commission from client subscriptions powered by RoniDoc."
      />


      {/* Stripe Connect Status */}
      {!connectStatus?.connected && (
        <Card className="border-[#5048e5]/20 bg-[#5048e5]/5">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#5048e5]/10 flex items-center justify-center">
                <Link2 className="h-6 w-6 text-[#5048e5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Enable Client Billing</h3>
                <p className="text-sm text-muted-foreground">
                  Connect your Stripe account to start earning commission from client subscriptions.
                  Your clients pay $4.90-$19.90/month and you keep 30% of every payment automatically.
                </p>
              </div>
              <Button onClick={handleOnboard} className="bg-[#5048e5] hover:bg-[#443fcc] shrink-0">
                <Link2 className="h-4 w-4 mr-2" />
                {connectStatus?.status === 'onboarding' ? 'Continue Setup' : 'Connect Stripe'}
              </Button>
            </div>
            {connectStatus?.status === 'onboarding' && (
              <p className="text-xs text-amber-600 mt-3">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                Stripe account setup is incomplete. Click "Continue Setup" to finish.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Connected + Earnings */}
      {connectStatus?.connected && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">This Month</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {earningsLoading ? '...' : fmtCurrency(earnings?.thisMonth || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">All Time</p>
                <p className="text-2xl font-bold mt-1">
                  {earningsLoading ? '...' : fmtCurrency(earnings?.allTime || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Clients</p>
                <p className="text-2xl font-bold mt-1">
                  {earningsLoading ? '...' : earnings?.activeClients || 0}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg / Client</p>
                <p className="text-2xl font-bold mt-1">
                  {earningsLoading ? '...' : fmtCurrency(earnings?.avgPerClient || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Plan Breakdown */}
          {earnings?.planBreakdown?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Client Plan Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  {earnings.planBreakdown.map((plan: any) => (
                    <div key={plan.display_name} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{plan.display_name}</Badge>
                      <span className="text-sm font-medium">{plan.count} clients</span>
                      <span className="text-xs text-muted-foreground">{fmtCurrency(parseFloat(plan.price_monthly))}/mo each</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Client Revenue Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Client Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              {clients && clients.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left p-2 font-medium">Client</th>
                        <th className="text-left p-2 font-medium">Plan</th>
                        <th className="text-right p-2 font-medium">Monthly</th>
                        <th className="text-right p-2 font-medium">Your Commission</th>
                        <th className="text-center p-2 font-medium">Status</th>
                        <th className="text-right p-2 font-medium">Total Earned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client: any) => (
                        <tr key={client.client_id} className="border-b hover:bg-muted/20">
                          <td className="p-2">
                            <div>
                              <p className="font-medium">{client.client_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{client.client_email}</p>
                            </div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">{client.plan_name}</Badge>
                          </td>
                          <td className="p-2 text-right">{fmtCurrency(parseFloat(client.price_monthly || 0))}</td>
                          <td className="p-2 text-right text-green-600 font-medium">
                            {fmtCurrency(parseFloat(client.price_monthly || 0) * 0.3)}
                          </td>
                          <td className="p-2 text-center">
                            <Badge className={cn('text-[10px]',
                              client.status === 'active' ? 'bg-green-100 text-green-700' :
                              client.status === 'past_due' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            )}>
                              {client.status}
                            </Badge>
                          </td>
                          <td className="p-2 text-right font-medium">{fmtCurrency(parseFloat(client.total_commission || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No client subscriptions yet</p>
                  <p className="text-xs mt-1">Invite clients to subscribe to start earning commission</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available Plans */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Client Plans Available</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {clientPlans?.map((plan: any) => (
                  <div key={plan.id} className="border rounded-lg p-4">
                    <h3 className="font-semibold">{plan.display_name}</h3>
                    <p className="text-2xl font-bold mt-1">${parseFloat(plan.price_monthly).toFixed(2)}<span className="text-sm text-muted-foreground font-normal">/mo</span></p>
                    <p className="text-xs text-green-600 mt-1">
                      You earn {fmtCurrency(parseFloat(plan.price_monthly) * 0.3)}/mo per client
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">{plan.description}</p>
                    <div className="mt-3 text-xs text-muted-foreground">
                      <p>Documents: {plan.max_documents === -1 ? 'Unlimited' : plan.max_documents}</p>
                      <p>AI Queries: {plan.max_ai_queries === -1 ? 'Unlimited' : (plan.max_ai_queries === 0 ? 'None' : `${plan.max_ai_queries}/mo`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
